import { useAppTFunction } from '@retail/app/i18n';
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { CreateOrUpdateMarkupForm, CreateOrUpdateMarkupSchema } from '@retail/markup/components';
import { useConditionActions } from '@retail/calculus/context';
import { withDialogSuspenseErrorBoundary } from '@retail/components';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { getConditionBindingValuesFromLevelSchema } from '@retail/calculus/components';
import { useGetActiveMarkupCondition } from '@retail/calculus/data-access';
import { CreateConditionDialog } from '../ConditionActionsBase';

interface Props {
  open: boolean;
  supplierNumber?: number;
  includeUnit?: boolean;
  onClose: () => void;
}

function CreateMarkupConditionDialog({ open, supplierNumber, includeUnit, onClose }: Props) {
  const t = useAppTFunction();
  const { assortmentId } = useSelectedOrgUnit();
  const { createOrUpdateCondition } = useConditionActions();

  const {
    handleSubmit,
    reset,
    formState: { errors },
    getValues,
    watch,
  } = useFormContext<CreateOrUpdateMarkupSchema>();
  const levelSchemaValues = watch(['level', 'item', 'overGroup', 'discountGroups', 'gtin']);

  const bindingLevel = useMemo(
    () => getConditionBindingValuesFromLevelSchema({ levelSchema: getValues(), supplierNumber })[0],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [levelSchemaValues, supplierNumber]
  );

  const { data: activeMarkupCondition } = useGetActiveMarkupCondition({
    assortmentId,
    bindingLevel: bindingLevel,
    enabled: open && !!bindingLevel,
  });

  const onActivate = useCallback(
    (levelSchema: CreateOrUpdateMarkupSchema) => {
      createOrUpdateCondition({
        content: { markup: { salesFactor: Number(levelSchema.salesFactor) } },
        bindingValue: getConditionBindingValuesFromLevelSchema({
          levelSchema,
          supplierNumber,
        }),
        validFrom: levelSchema.fromDate,
      }).then(() => {
        reset();
        onClose();
      });
    },
    [createOrUpdateCondition, supplierNumber, reset, onClose]
  );

  return (
    <CreateConditionDialog
      open={open}
      title={t('markup.create.newMarkup')}
      supplierNumber={supplierNumber}
      includePackages={includeUnit}
      dialogContent={
        <form>
          <CreateOrUpdateMarkupForm markupCondition={activeMarkupCondition} />
        </form>
      }
      isDisabled={Object.keys(errors).length > 0}
      onSubmit={handleSubmit(onActivate)}
      onClose={onClose}
    />
  );
}

export default withDialogSuspenseErrorBoundary(CreateMarkupConditionDialog);
